/**
 * `useProjectTaskUpdatesForDate` — live feed of every work-log
 * update across every task on the project, filtered to a single
 * calendar day.
 *
 * Web SDK port of mobile's
 *   /Users/gudupuramesh/Documents/siteexpens/src/features/tasks/useProjectTaskUpdatesForDate.ts
 *
 * Same Firestore paths (`tasks/{taskId}/updates`), same sort order
 * (newest first). The Site tab uses this to render the day's
 * "Timeline updates" panel; the Timeline tab uses the same feed
 * keyed by today to surface live activity.
 *
 * Strategy: one snapshot listener per task, range-filtered by
 * createdAt within [dayStart, dayEnd). Aggregated in a Map by
 * taskId, merged + sorted on every tick. Avoids the
 * collectionGroup query (which Firestore rules would require an
 * extra index for).
 */
import {
  Timestamp,
  collection,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';

import { db } from '@/lib/firebase';

export type TaskUpdateDoc = {
  id: string;
  authorId: string;
  authorName: string;
  progress: number;
  text: string;
  photoUris: string[];
  createdAt: { toMillis(): number } | null;
};

export type ProjectTaskUpdateRow = TaskUpdateDoc & {
  taskId: string;
  taskTitle: string;
};

export type UseProjectTaskUpdatesForDateResult = {
  data: ProjectTaskUpdateRow[];
  loading: boolean;
};

function dayBounds(dateKey: string): { start: Date; end: Date } {
  const [y, m, d] = dateKey.split('-').map((s) => parseInt(s, 10));
  const start = new Date(y, (m ?? 1) - 1, d ?? 1, 0, 0, 0, 0);
  const end = new Date(y, (m ?? 1) - 1, d ?? 1, 23, 59, 59, 999);
  return { start, end };
}

export function useProjectTaskUpdatesForDate(
  projectId: string | undefined,
  dateKey: string,
  tasks: { id: string; title: string }[],
): UseProjectTaskUpdatesForDateResult {
  // Stable signature of the task refs so the effect doesn't
  // re-subscribe on every parent render. Sorted by id.
  const taskKey = useMemo(
    () =>
      tasks
        .map((t) => `${t.id}|${t.title.replace(/\|/g, '/')}`)
        .sort()
        .join(','),
    [tasks],
  );

  const [byTask, setByTask] = useState<
    Map<string, ProjectTaskUpdateRow[]>
  >(new Map());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId || tasks.length === 0) {
      setByTask(new Map());
      setLoading(false);
      return;
    }

    setLoading(true);
    const { start, end } = dayBounds(dateKey);
    const startTs = Timestamp.fromDate(start);
    const endTs = Timestamp.fromDate(end);

    const unsubs: (() => void)[] = [];
    const pending = new Set(tasks.map((t) => t.id));

    for (const t of tasks) {
      const unsub = onSnapshot(
        query(
          collection(db, 'tasks', t.id, 'updates'),
          where('createdAt', '>=', startTs),
          where('createdAt', '<=', endTs),
        ),
        (snap) => {
          const rows: ProjectTaskUpdateRow[] = snap.docs.map((d) => {
            const data = d.data() as Omit<TaskUpdateDoc, 'id'>;
            return {
              id: d.id,
              authorId: data.authorId ?? '',
              authorName: data.authorName ?? 'Member',
              progress:
                typeof data.progress === 'number' ? data.progress : 0,
              text: data.text ?? '',
              photoUris: Array.isArray(data.photoUris) ? data.photoUris : [],
              createdAt: data.createdAt ?? null,
              taskId: t.id,
              taskTitle: t.title,
            };
          });
          setByTask((prev) => {
            const next = new Map(prev);
            next.set(t.id, rows);
            return next;
          });
          pending.delete(t.id);
          if (pending.size === 0) setLoading(false);
        },
        (err) => {
          console.warn(
            `[useProjectTaskUpdatesForDate] snapshot error for task ${t.id}:`,
            err,
          );
          pending.delete(t.id);
          if (pending.size === 0) setLoading(false);
        },
      );
      unsubs.push(unsub);
    }

    return () => {
      for (const u of unsubs) u();
    };
    // taskKey is the stable signature; dateKey + projectId drive resub.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, dateKey, taskKey]);

  // Merge + sort newest first.
  const data = useMemo(() => {
    const all: ProjectTaskUpdateRow[] = [];
    for (const rows of byTask.values()) all.push(...rows);
    all.sort((a, b) => {
      const am = a.createdAt?.toMillis?.() ?? 0;
      const bm = b.createdAt?.toMillis?.() ?? 0;
      return bm - am;
    });
    return all;
  }, [byTask]);

  return { data, loading };
}

/** Format "Xm ago" / "Xh ago" / "yesterday" for the feed row. */
export function relativeTime(ms: number | null | undefined): string {
  if (!ms) return '';
  const diff = Date.now() - ms;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'yesterday';
  return `${days}d ago`;
}
